import { useState, useEffect } from 'react';
import axios from 'axios';
import TopBar from '../components/TopBar';
import Header from '../components/Header';
import Footer from '../components/Footer';
import NoticeBar from '../components/NoticeBar';
import HeroCarousel from '../components/HeroCarousel';
import StatementOfPurpose from '../components/StatementOfPurpose';
import Mission from '../components/Mission';
import Petition from '../components/Petition';
import WhoWeAre from '../components/WhoWeAre';
import Articles from '../components/Articles';
import HomepagePopup from '../components/HomepagePopup';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Homepage editorial copy is authoritative over the legacy site endpoint.
const PURPOSE_AND_MISSION = {
  statement_title: "Statement of Purpose",
  statement_text: "Secure the Vote Maryland is a 501(c)(3) nonprofit organization committed to promoting transparent, lawful, and trustworthy elections throughout Maryland.\nOur purpose is to educate Maryland citizens about the laws, policies, procedures, and systems that govern our elections; conduct and share research concerning election administration; encourage transparency and accountability; and provide citizens with the information and resources necessary to understand and participate in the election process.",
  mission_title: "Our Mission",
  mission_text: "The mission of Secure the Vote Maryland is to educate and empower Maryland citizens to become informed participants in the election process and to promote transparency, accountability, accuracy, and lawful administration throughout Maryland's election system. We believe public confidence in our elections is strengthened when election laws are followed, election procedures are transparent, voter registration records are accurate, and citizens have meaningful access to information about how their elections are administered."
};

// Homepage copy is maintained here because /content/site still serves legacy wording.
const WHO_WE_ARE_CONTENT = {
  who_we_are_title: "Who We Are",
  who_we_are_text: "Secure the Vote Maryland is a 501(c)(3) nonprofit organization dedicated to educating Maryland citizens about our election system and promoting transparency, accountability, accuracy, and public confidence in the administration of our elections.\n\nWe are Maryland citizens and volunteers who believe that secure and trustworthy elections are fundamental to our Constitutional Republic. Our team works to better understand the laws, policies, procedures, voter registration systems, and administrative practices that shape elections throughout our state—and to make that information understandable and accessible to the public.\n\nThrough research, public education, citizen engagement, election observation, and collaboration with individuals and organizations that share our commitment to election integrity, Secure the Vote Maryland provides Marylanders with information and resources to become more knowledgeable and engaged participants in the election process.\n\nOur work is driven by a simple principle:\nEvery eligible voter should be able to participate in an election system that is lawful, transparent, accurate, and worthy of the public's trust."
};

// Static fallback so the page renders instantly
const STATIC_CONTENT = {
  ...PURPOSE_AND_MISSION,
  petition_title: "Did You Sign the Petition?",
  petition_cta_text: "Read More",
  petition_cta_url: "/sign-the-petition",
  ...WHO_WE_ARE_CONTENT
};

const STATIC_HEADER = {
  logo_text: "Secure the Vote",
  logo_subtitle: "MARYLAND",
  cta_text: "DONATE NOW",
  cta_url: "/donate"
};

const STATIC_FOOTER = {
  copyright_text: "\u00a9 2025 Secure the Vote Maryland. All rights reserved.",
  contact_email: "info@securethevotemd.com",
  contact_phone: ""
};

const HomePage = () => {
  const [headerConfig, setHeaderConfig] = useState(STATIC_HEADER);
  const [footerConfig, setFooterConfig] = useState(STATIC_FOOTER);
  const [heroSlides, setHeroSlides] = useState([]);
  const [noticeBar, setNoticeBar] = useState(null);
  const [siteContent, setSiteContent] = useState(STATIC_CONTENT);
  const [articles, setArticles] = useState([]);
  const [homepagePopup, setHomepagePopup] = useState(null);

  useEffect(() => {
    // Fetch in background — page is already rendered with static content
    const fetchContent = async () => {
      try {
        const [headerRes, footerRes, heroRes, noticeRes, contentRes, articlesRes, popupRes] = await Promise.all([
          axios.get(`${API_URL}/api/content/header`),
          axios.get(`${API_URL}/api/content/footer`),
          axios.get(`${API_URL}/api/content/hero-slides`),
          axios.get(`${API_URL}/api/content/notice-bar`),
          axios.get(`${API_URL}/api/content/site`),
          axios.get(`${API_URL}/api/content/articles`),
          axios.get(`${API_URL}/api/content/homepage-popup`).catch(() => ({ data: null }))
        ]);
        
        setHeaderConfig(headerRes.data);
        setFooterConfig(footerRes.data);
        setHeroSlides(heroRes.data);
        setNoticeBar(noticeRes.data);
        setSiteContent({ ...STATIC_CONTENT, ...contentRes.data, ...PURPOSE_AND_MISSION, ...WHO_WE_ARE_CONTENT });
        setArticles(articlesRes.data);
        setHomepagePopup(popupRes.data);
      } catch (error) {
        console.error('Error fetching content:', error);
      }
    };

    fetchContent();
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <TopBar config={footerConfig} />
      <Header config={headerConfig} />
      
      <main>
        {noticeBar?.enabled && <NoticeBar config={noticeBar} />}
        <HeroCarousel slides={heroSlides} />
        <StatementOfPurpose content={siteContent} />
        <Mission content={siteContent} />
        <Petition content={siteContent} />
        <WhoWeAre content={siteContent} />
        <Articles articles={articles} />
      </main>
      <Footer config={footerConfig} />
      <HomepagePopup config={homepagePopup} />
    </div>
  );
};

export default HomePage;
